import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

export default function LeadForm({ source }: { source?: string }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const questions = [
    {
      title: "What is your primary business goal right now?",
      options: ["Generate more inbound leads", "Improve lead quality & conversion", "Automate my follow-up & save time", "Scale my existing successful campaigns"]
    },
    {
      title: "What is your biggest marketing bottleneck?",
      options: ["My website doesn't convert visitors", "I'm buying shared/low-quality leads", "My team takes too long to follow up", "I don't know my exact ROI/CPA"]
    },
    {
      title: "What is your current monthly marketing budget?",
      options: ["Under $1,000", "$1,000 - $3,000", "$3,000 - $10,000", "$10,000+"]
    },
    {
      title: "How soon are you looking to implement a new system?",
      options: ["ASAP (Ready to start)", "Within 2 weeks", "Within 1 month", "Just exploring"]
    }
  ];

  const handleAnswer = (answer: string) => {
    setAnswers(prev => ({ ...prev, [step]: answer }));
    setStep(step + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch("https://services.leadconnectorhq.com/hooks/b5IpxWX8b85tEC05kVos/webhook-trigger/eb3a59f1-31e6-47f5-b08c-08f852159959", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: formData.name.split(" ")[0],
          last_name: formData.name.split(" ").slice(1).join(" "),
          email: formData.email,
          phone: formData.phone,
          companyName: formData.company,
          primary_business_goal: answers[0],
          marketing_bottleneck: answers[1],
          monthly_marketing_budget: answers[2],
          implementation_timeline: answers[3],
          source: source || "Lead Form"
        })
      });
    } catch (error) {
      console.error("Error submitting form", error);
    }
    
    setIsSubmitting(false);
    setIsSuccess(true);
  };

  if (isSuccess) {
    return (
      <div className="text-center py-8 animate-in fade-in zoom-in-95 duration-500">
        <div className="w-16 h-16 bg-accent/10 text-accent flex items-center justify-center mx-auto mb-6 border border-accent/30">
          <Sparkles className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold mb-4">Audit Request Received!</h3>
        <p className="text-muted-foreground mb-8">
          We're analyzing your responses and will be in touch shortly with your custom growth blueprint.
        </p>
        <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold px-8 rounded-none" onClick={() => { setStep(0); setIsSuccess(false); setAnswers({}); setFormData({ name: "", email: "", phone: "", company: "" }); }}>
          Start Over
        </Button>
      </div>
    );
  }

  if (step < questions.length) {
    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-500">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-1 bg-primary/10">
            <div 
              className="h-full bg-accent transition-all duration-500" 
              style={{ width: `${((step) / (questions.length + 1)) * 100}%` }} 
            />
          </div>
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            Step {step + 1} of {questions.length + 1}
          </span>
        </div>

        <h3 className="text-xl font-bold mb-4">{questions[step].title}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {questions[step].options.map((opt, i) => (
            <button 
              key={i}
              type="button"
              onClick={() => handleAnswer(opt)}
              className="p-4 text-left border border-primary/20 hover:border-accent hover:bg-accent/5 hover:shadow-md transition-all focus:outline-none focus:ring-1 focus:ring-accent group"
            >
              <span className="block text-sm font-bold text-primary group-hover:text-accent mb-1 transition-colors">Option {String.fromCharCode(65 + i)}</span>
              <span className="block text-sm text-foreground font-medium">{opt}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <form className="space-y-4 animate-in fade-in slide-in-from-right-8 duration-500" onSubmit={handleSubmit}>
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 h-1 bg-primary/10">
          <div 
            className="h-full bg-accent transition-all duration-500" 
            style={{ width: `100%` }} 
          />
        </div>
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          Final Step
        </span>
      </div>

      <h3 className="text-xl font-bold mb-4">Where should we send your audit?</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="name" className="text-sm font-medium text-foreground">Full Name</label>
          <input 
            id="name" 
            type="text" 
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="John Doe" 
            className="w-full px-4 py-3 bg-secondary border border-primary/30 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all placeholder:text-muted-foreground/50"
            required
          />
        </div>
        
        <div className="space-y-1.5">
          <label htmlFor="email" className="text-sm font-medium text-foreground">Email Address</label>
          <input 
            id="email" 
            type="email" 
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="john@example.com" 
            className="w-full px-4 py-3 bg-secondary border border-primary/30 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all placeholder:text-muted-foreground/50"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="phone" className="text-sm font-medium text-foreground">Phone Number</label>
          <input 
            id="phone" 
            type="tel" 
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            placeholder="(555) 123-4567" 
            className="w-full px-4 py-3 bg-secondary border border-primary/30 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all placeholder:text-muted-foreground/50"
            required
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="company" className="text-sm font-medium text-foreground">Business Name</label>
          <input 
            id="company" 
            type="text" 
            value={formData.company}
            onChange={(e) => setFormData({...formData, company: e.target.value})}
            placeholder="Acme Corp" 
            className="w-full px-4 py-3 bg-secondary border border-primary/30 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all placeholder:text-muted-foreground/50"
            required
          />
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 text-lg mt-4 shadow-lg border border-primary rounded-none">
        {isSubmitting ? "Processing..." : "Get My Free Growth Audit"}
      </Button>
      
      <p className="text-[10px] text-center text-muted-foreground mt-4 leading-tight">
        By clicking "Get My Free Growth Audit", you agree to our <a href="/terms" className="underline hover:text-primary">Terms</a> and <a href="/privacy" className="underline hover:text-primary">Privacy Policy</a>. You consent to receive recurring automated marketing and informational text messages from DANU MARKETING. Consent is not a condition of purchase. Message and data rates may apply. Reply STOP to opt-out.
      </p>
      
      <p className="text-[10px] text-center text-muted-foreground mt-2 flex items-center justify-center gap-1.5 uppercase tracking-widest font-bold">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
        Secure & Private
      </p>
    </form>
  );
}
